import { useTranslation } from "react-i18next";
import { useFeatureFlagEnabled } from "posthog-js/react";
import type { OrderTemplate } from "@shared/schema";
import { TemplateCardGrid } from "@/components/orders/TemplateCardGrid";
import { PhotoToOrderPanel } from "@/components/orders/PhotoToOrderPanel";
import { cn } from "@/lib/utils";

interface QuickStartSectionProps {
  templates: OrderTemplate[];
  templatesLoading: boolean;
  templatesError?: boolean;
  onSelectTemplate: (templateId: string) => void;
  disabled?: boolean;
}

export function QuickStartSection({
  templates,
  templatesLoading,
  templatesError = false,
  onSelectTemplate,
  disabled = false,
}: QuickStartSectionProps) {
  const { t } = useTranslation();
  const isTextListEnabled = useFeatureFlagEnabled("text-lto") ?? false;
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold" data-step-heading tabIndex={-1}>{t("components.orders.quickStartSection.startYourOrder")}</h2>
      <div className="grid gap-4 md:grid-cols-[1fr_auto] min-w-0">
        <div className="rounded-lg border-2 border-border bg-card p-4 space-y-2 shadow-sm min-w-0 overflow-hidden">
          <p className="text-sm font-semibold">
            {t("components.orders.quickStartSection.orderFromATemplate")}
          </p>
          <TemplateCardGrid
            templates={templates}
            isLoading={templatesLoading}
            isError={templatesError}
            onSelectTemplate={onSelectTemplate}
            disabled={disabled}
          />
        </div>
        <div
          className={cn(
            // Width and padding are identical in both branches: the templates
            // panel beside this is the grid's 1fr column, so anything taken here
            // comes out of its cards-per-row budget and pushes templates to wrap
            // onto more rows. Extra room for the two buttons comes from the gap.
            "rounded-lg border-2 border-border bg-card flex md:flex-col items-center justify-center shadow-sm p-3 md:w-36",
            isTextListEnabled ? "gap-3" : "gap-2",
          )}
        >
          {/* Same treatment as the "Order from a Template" header beside it. */}
          <p className="text-sm font-semibold md:text-center">
            {isTextListEnabled
              ? t("components.orders.quickStartSection.uploadAList")
              : t("components.orders.quickStartSection.uploadAFile")}
          </p>
          <PhotoToOrderPanel disabled={disabled} />
        </div>
      </div>
    </div>
  );
}
